import PropTypes from 'prop-types'
import { useRef } from 'react'
import R from 'ramda'
import styled from 'styled-components'
import Datetime from 'react-datetime'

import {
  Box,
  Icon,
  TransparentButton,
  InputWrapper,
  inputIconWidth,
  inputStyles,
} from '@redriverpress/system'

const StyledInput = styled.input`
  ${inputStyles}
  line-height: 1.4;
  background: transparent;
  cursor: pointer;
`

export default function DateTimePicker({
  value,
  placeholder,
  closeOnSelect,
  includeTime,
  disabled,
  error,
  icon,
  label,
  onChange,
  isValidDate,
  onOpen,
  onClose,
  onReset,
  ...props
}) {
  const ref = useRef(null)

  // https://github.com/arqex/react-datetime/issues/760#issuecomment-794381081
  const handleReset = () => {
    ref.current.state.inputValue = ''
    onReset()
  }

  return (
    <InputWrapper icon={icon} label={label} error={error}>
      <Datetime
        renderInput={inputProps => {
          return (
            <Box position='relative'>
              <StyledInput
                pt={label ? '1.5rem' : 2}
                pl={icon ? inputIconWidth : 3}
                pr={onReset ? inputIconWidth : 3}
                pb={2}
                {...props}
                {...inputProps}
                disabled={disabled}
                placeholder={placeholder}
                readOnly
                error={Boolean(error)}
              />
              {value && onReset && (
                <TransparentButton
                  position='absolute'
                  top={0}
                  bottom={0}
                  right={0}
                  p={0}
                  width={inputIconWidth}
                  onClick={handleReset}
                >
                  <Icon icon='times' />
                </TransparentButton>
              )}
            </Box>
          )
        }}
        ref={ref}
        onChange={data => {
          // workaround because library's closeonselect isn't working
          if (closeOnSelect) {
            setTimeout(ref.current.closeCalendar, 1)
            onClose()
          }
          onChange(data.toDate())
        }}
        value={value}
        dateFormat={`MMM D, YYYY${includeTime ? ',' : ''}`}
        timeFormat={includeTime && '[at] h:mm a'}
        isValidDate={date => isValidDate(date.toDate())}
        onOpen={onOpen}
        onClose={onClose}
      />
    </InputWrapper>
  )
}

DateTimePicker.defaultProps = {
  value: null,
  disabled: false,
  error: false,
  icon: 'calendar-alt',
  label: null,
  placeholder: 'Please select…',
  closeOnSelect: true,
  includeTime: false,
  isValidDate: R.T,
  onOpen: () => ({}),
  onClose: () => ({}),
  onReset: null,
}

DateTimePicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.bool,
  ]),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isValidDate: PropTypes.func,
  placeholder: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  includeTime: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
}
